import { useAuthContext } from "providers/Auth";
import { PartnerFeatures } from "shared/types";
import { FEATURES } from "types/landlord/partner";

/**
 * UI feature flag holding Owner reports back while it ships dark (BSCPF-6078).
 *
 * A `ui_` registry flag (Boom::UiFeatureFlag), so the key arrives in the
 * partner's `features` array and is toggled per partner from admin's
 * /ui-feature-flags page — no client deploy to flip it, and it works locally
 * where PostHog has no token and every PostHog flag reads as undefined.
 *
 * A partner whose features have not loaded counts as OFF, so nobody sees the
 * feature before their features are known.
 */
export const useOwnerReportsVisible = () => {
  const { partner } = useAuthContext();

  return Boolean(
    partner?.features.includes(FEATURES.UI_SCREENING_OWNER_REPORTS)
  );
};

/**
 * Whether a partner may actually use Owner reports: allowed to see it at all,
 * and having switched it on themselves.
 *
 * Two different gates, deliberately kept apart. The flag is Boom's rollout
 * control; `owner_screening_reports` is the partner's own switch on Settings →
 * Features. Every surface downstream of the switch asks for both, so turning
 * the flag off is a true kill switch even for a partner who already switched
 * the feature on.
 *
 * NOTE: the owner's own page is deliberately NOT gated on either — the ticket
 * requires links already sent to keep working after the feature is turned off.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * REMOVING THE FLAG once Owner reports is generally available:
 *
 *  1. Delete this file.
 *  2. `views/settings-features/index.tsx` — drop the filter, so PARTNER_FEATURES
 *     renders both cards again.
 *  3. `views/settings/index.tsx` — drop `ownerReportsVisible` from the
 *     `owner_reports` branch, leaving the partner switch and admin check.
 *  4. `views/applications-id/components/LayoutHeader/hooks/usePopoverActions.tsx`
 *     — drop the flag from `showShareWithOwner`.
 *  5. `types/landlord/partner.ts` — drop `UI_SCREENING_OWNER_REPORTS`.
 *  6. Delete the `ui_screening_owner_reports` row in admin's /ui-feature-flags.
 *
 * The sites in steps 2–4 are marked with an `OWNER REPORTS FLAG` comment.
 */
export const useOwnerReportsEnabled = () => {
  const { partnerFeatures } = useAuthContext();
  const visible = useOwnerReportsVisible();

  return visible && partnerFeatures.includes(PartnerFeatures.OwnerReports);
};
